/**
 * AskPage — AskBot chat UI
 *
 * Layout (mobile-first, one-handed):
 *   ┌─────────────────────┐
 *   │ Header              │  shrink-0
 *   ├─────────────────────┤
 *   │ Scroll area         │  flex-1 overflow-y-auto
 *   │  (empty state OR    │
 *   │   conversation)     │
 *   ├─────────────────────┤
 *   │ Autocomplete chips  │  shrink-0 — shown while typing
 *   │ Input bar           │  shrink-0
 *   └─────────────────────┘
 *
 * Every primary action (input, send, follow-up chips) lives
 * in the bottom 60% of the screen on a 375px device.
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { Send, ArrowLeft, Bot, User, Sparkles, RotateCcw, Clock, Trash2, ChevronRight, Download, ExternalLink, FileText, Image as ImageIcon, Copy, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import {
  processAskBotQuery,
  UserSecurityContext,
  ConversationTurn,
  AskBotProgressStep,
  AskBotResult,
  AskBotClarification,
} from "@/services/askbotService";
import { getAutocompleteSuggestions } from "@/services/askbotSuggestions";
import ReactMarkdown from "react-markdown";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

import { AnswerCard } from "@/components/askbot/AnswerCard";
import { buildProductNotesLink } from "@/lib/askbotDeeplinks";
import {
  getRecentQueries,
  addRecentQuery,
  clearRecentQueries,
} from "@/components/askbot/RecentQueries";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  followUpQuestions?: string[];
  data?: AskBotResult["data"];
  productRef?: AskBotResult["productRef"];
}

// ---------------------------------------------------------------------------
// Product notes deep link
// ---------------------------------------------------------------------------

/**
 * Jump from a product answer into Banks & Products, pre-selected to the same
 * bank and loan type the bot answered from. Renders nothing when either slot
 * is missing, since the target page cannot be seeded without both.
 */
function ProductNotesLink({
  productRef,
}: {
  productRef: NonNullable<AskBotResult["productRef"]>;
}) {
  const navigate = useNavigate();
  const to = buildProductNotesLink(productRef);
  if (!to) return null;

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => navigate(to)}
      className="h-8 w-full justify-start gap-1.5 text-xs"
    >
      <ExternalLink className="h-3.5 w-3.5 shrink-0" />
      Open in Banks &amp; Products
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Typing indicator — step trail + rotating context messages
// ---------------------------------------------------------------------------

const STEP_ORDER: AskBotProgressStep[] = [
  "classifying",
  "planning",
  "fetching",
];

const STEP_META: Record<
  AskBotProgressStep,
  { label: string; messages: string[] }
> = {
  classifying: {
    label: "Understanding",
    messages: [
      "Reading your question…",
      "Figuring out what you need…",
      "Identifying the right data…",
      "Parsing your intent…",
    ],
  },
  planning: {
    label: "Planning",
    messages: [
      "Planning the data query…",
      "Choosing what to look up…",
      "Mapping request to your CRM…",
      "Deciding which records to fetch…",
    ],
  },
  fetching: {
    label: "Fetching",
    messages: [
      "Searching your CRM…",
      "Looking through your records…",
      "Pulling live data…",
      "Scanning leads and contacts…",
      "Almost done fetching…",
    ],
  },
  responding: {
    label: "Responding",
    messages: [
      "Writing your answer…",
      "Putting it all together…",
      "Almost ready…",
    ],
  },
};

function TypingIndicator({ step }: { step: AskBotProgressStep | null }) {
  const [msgIdx, setMsgIdx] = useState(0);

  useEffect(() => {
    setMsgIdx(0);
  }, [step]);

  useEffect(() => {
    if (!step) return;
    const msgs = STEP_META[step].messages;
    if (msgs.length <= 1) return;
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % msgs.length), 1800);
    return () => clearInterval(t);
  }, [step]);

  const currentIdx = step ? STEP_ORDER.indexOf(step) : -1;
  const label =
    step && currentIdx >= 0
      ? STEP_META[step].messages[msgIdx]
      : step === "responding"
        ? (STEP_META.responding.messages[msgIdx] ?? "Writing answer…")
        : "Thinking…";

  return (
    <div className="bg-muted border border-border/60 rounded-2xl rounded-bl-sm px-3.5 py-2.5">
      <div className="flex items-center gap-2">
        {/* Compact step dot trail */}
        <div className="flex items-center gap-1 shrink-0">
          {STEP_ORDER.map((s, i) => {
            const isDone = currentIdx > i;
            const isCurrent = currentIdx === i;
            return (
              <div key={s} className="flex items-center gap-1">
                <div
                  className={cn(
                    "h-2 w-2 rounded-full transition-all duration-300",
                    isDone
                      ? "bg-violet-500"
                      : isCurrent
                        ? "bg-violet-400 ring-2 ring-violet-400/30"
                        : "bg-muted-foreground/20",
                  )}
                />
                {i < STEP_ORDER.length - 1 && (
                  <div
                    className={cn(
                      "h-px w-2 shrink-0 transition-colors duration-300",
                      isDone ? "bg-violet-400/60" : "bg-border/50",
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Current step label */}
        <span className="text-[10px] font-semibold text-violet-600 shrink-0">
          {step && currentIdx >= 0
            ? STEP_META[step].label
            : step
              ? STEP_META[step].label
              : ""}
        </span>

        <span className="text-muted-foreground/40 text-[10px] shrink-0">·</span>

        {/* Rotating message */}
        <AnimatePresence mode="wait">
          <motion.span
            key={`${step}-${msgIdx}`}
            initial={{ opacity: 0, y: 3 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -3 }}
            transition={{ duration: 0.15 }}
            className="text-[11px] text-muted-foreground truncate"
          >
            {label}
          </motion.span>
        </AnimatePresence>
      </div>
    </div>
  );
}

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

type PreviewType = "image" | "pdf" | "other";
interface PreviewFile {
  url: string;
  name: string;
  type: PreviewType;
}

const IMAGE_EXTS = new Set(["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"]);

function getFileType(url: string): PreviewType {
  try {
    // strip query params (e.g. Firebase Storage tokens) before reading extension
    const pathname = new URL(url).pathname;
    const ext = pathname.split(".").pop()?.toLowerCase() ?? "";
    if (IMAGE_EXTS.has(ext)) return "image";
    if (ext === "pdf") return "pdf";
  } catch {
    /* relative or malformed URL */
  }
  return "other";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AskPage = () => {
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [history, setHistory] = useState<ConversationTurn[]>([]);
  /**
   * Signed slot-filling state from the last turn, when the bot asked a
   * follow-up question. Held here rather than derived from `history` because
   * it must survive verbatim — the server signs it.
   */
  const [clarification, setClarification] = useState<AskBotClarification | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState<AskBotProgressStep | null>(
    null,
  );
  const [streamingMsgId, setStreamingMsgId] = useState<string | null>(null);
  const [recentQueries, setRecentQueries] = useState<string[]>([]);
  const [autocompletes, setAutocompletes] = useState<string[]>([]);
  const [recentOpen, setRecentOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null);
  /** id of the message whose copy button is showing its "copied" tick */
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const navigate = useNavigate();
  const { user } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const userId = user?.id ?? user?.userId ?? "";

  // Load recent queries from localStorage on mount
  useEffect(() => {
    if (userId) setRecentQueries(getRecentQueries(userId));
  }, [userId]);

  const scrollToBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // Autocomplete chips when typing (≥2 chars)
  useEffect(() => {
    if (query.length >= 2) {
      setAutocompletes(
        getAutocompleteSuggestions(query, user?.primaryRole ?? ""),
      );
    } else {
      setAutocompletes([]);
    }
  }, [query, user?.primaryRole]);

  const clearConversation = useCallback(() => {
    setMessages([]);
    setHistory([]);
    // Drop any half-answered question too, or the first message of the new
    // conversation gets absorbed as the answer to one the user just cleared.
    setClarification(null);
    setQuery("");
    setAutocompletes([]);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  // -------------------------------------------------------------------------
  // Core send handler
  // -------------------------------------------------------------------------

  const sendQuery = async (queryText: string) => {
    if (!queryText.trim() || isLoading) return;
    if (!user?.id && !user?.userId) {
      console.error("[AskPage] No authenticated user");
      return;
    }

    const securityCtx: UserSecurityContext = {
      userId: user.id ?? user.userId ?? "",
      roles: user.roles ?? [],
      primaryRole: user.primaryRole ?? "",
      officeId: user.officeId,
    };

    const userMsg: Message = {
      id: `u-${Date.now()}`,
      role: "user",
      content: queryText.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setQuery("");
    setAutocompletes([]);
    setIsLoading(true);
    setLoadingStep("planning");

    const historySnapshot = history;
    const msgId = `a-${Date.now() + 1}`;
    let streamingStarted = false;

    // Persist to recents
    if (userId) {
      addRecentQuery(userId, queryText.trim());
      setRecentQueries(getRecentQueries(userId));
    }

    try {
      const result = await processAskBotQuery(
        queryText.trim(),
        securityCtx,
        historySnapshot,
        (step, earlyData) => {
          setLoadingStep(step);
          if (step === "responding" && !streamingStarted) {
            streamingStarted = true;
            setStreamingMsgId(msgId);
            setMessages((prev) => [
              ...prev,
              {
                id: msgId,
                role: "assistant" as const,
                content: "",
                timestamp: new Date(),
                data: earlyData,
              },
            ]);
          }
        },
        (chunk) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === msgId ? { ...m, content: m.content + chunk } : m,
            ),
          );
        },
        clarification ?? undefined,
      );

      // Carry (or drop) the slot-filling state. When the bot asks "which
      // city?", the next message is the answer and has no routable signal of
      // its own — this is what lets the server pick the conversation back up.
      setClarification(result.clarification ?? null);

      // Attach structured data + follow-ups once streaming finishes
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msgId
            ? {
                ...m,
                followUpQuestions: result.followUpQuestions,
                data: result.data,
                productRef: result.productRef,
              }
            : m,
        ),
      );

      setHistory((prev) =>
        [
          ...prev,
          { role: "user" as const, content: queryText.trim() },
          { role: "assistant" as const, content: result.answer.slice(0, 400) },
        ].slice(-12),
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Something went wrong";
      if (streamingStarted) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msgId
              ? {
                  ...m,
                  content: m.content
                    ? `${m.content}\n\n_Error: ${message}. Please try again._`
                    : `Sorry, ${message}. Please try again.`,
                }
              : m,
          ),
        );
      } else {
        setMessages((prev) => [
          ...prev,
          {
            id: `e-${Date.now()}`,
            role: "assistant" as const,
            content: `Sorry, ${message}. Please try again.`,
            timestamp: new Date(),
          },
        ]);
      }
    } finally {
      setIsLoading(false);
      setLoadingStep(null);
      setStreamingMsgId(null);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  // -------------------------------------------------------------------------
  // Input handlers
  // -------------------------------------------------------------------------

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendQuery(query);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setQuery(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = `${Math.min(e.target.scrollHeight, 100)}px`;
  };

  /**
   * Copy a sent question back to the clipboard. The tick is per-message so two
   * identical questions in one thread don't both light up.
   */
  const handleCopyMessage = async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success("Copied to clipboard");
      setTimeout(
        () => setCopiedId((current) => (current === id ? null : current)),
        2000,
      );
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  };

  const isEmpty = messages.length === 0;
  const firstName = user?.name?.split(" ")[0] ?? "there";

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <AppLayout>
      {/*
        Height must fit between the status-bar inset (#root adds padding-top:
        env(safe-area-inset-top)) and whatever AppLayout reserves on <main> for
        the fixed MobileNav — the nav's real height on mobile, 0 on desktop
        where it is hidden. A hardcoded 4rem matched neither.
      */}
      <div
        className="flex flex-col overflow-hidden"
        style={{
          height:
            "calc(100dvh - env(safe-area-inset-top, 0px) - var(--app-footer-reserve, 5rem))",
          maxHeight:
            "calc(100dvh - env(safe-area-inset-top, 0px) - var(--app-footer-reserve, 5rem))",
        }}
      >
        {/* ── Header — matches Admin Dashboard layout exactly ──────────── */}
        <div
          className="border-b bg-background shrink-0 px-4 pb-3"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 14px)" }}
        >
          {/* Single row: back arrow | title+subtitle | right action */}
          <div className="flex items-start gap-3">
            {/* Back button — aligned to first line of text */}
            <Button
              variant="ghost"
              size="icon"
              aria-label="Back"
              className="h-9 w-9 shrink-0 mt-0.5 -ml-1"
              onClick={() => navigate("/tools")}
            >
              <ArrowLeft className="h-[18px] w-[18px]" />
            </Button>

            {/* Title + subtitle — fills available space */}
            <div className="flex-1 min-w-0 text-center">
              <h1 className="text-[22px] font-bold leading-tight tracking-tight text-foreground">
                AskBot
              </h1>
              <p className="text-[13px] text-muted-foreground mt-0.5">
                CRM assistant
              </p>
            </div>

            {/* Right-side: history button styled like the refresh icon in admin */}
            <div className="flex items-center gap-2 mt-1 shrink-0">
              <Sheet open={recentOpen} onOpenChange={setRecentOpen}>
                <SheetTrigger asChild>
                  <button
                    type="button"
                    aria-label="History"
                    disabled={recentQueries.length === 0}
                    className="flex items-center justify-center h-8 w-8 rounded-lg border border-border bg-background disabled:opacity-30 active:bg-muted transition-colors"
                    title="History"
                  >
                    <Clock className="h-4 w-4 text-foreground" />
                  </button>
                </SheetTrigger>
                <SheetContent
                  side="bottom"
                  className="rounded-t-2xl max-h-[70dvh]"
                >
                  <SheetHeader className="text-left pb-2">
                    <div className="flex items-center justify-between">
                      <SheetTitle className="text-base">
                        Recent Questions
                      </SheetTitle>
                      {recentQueries.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-muted-foreground gap-1"
                          onClick={() => {
                            clearRecentQueries(userId);
                            setRecentQueries([]);
                            setRecentOpen(false);
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                          Clear
                        </Button>
                      )}
                    </div>
                  </SheetHeader>
                  <div className="overflow-y-auto space-y-1 pb-6 mt-2">
                    {recentQueries.map((q, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setRecentOpen(false);
                          sendQuery(q);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-muted/60 transition-colors active:bg-muted"
                      >
                        <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="text-sm flex-1 truncate">{q}</span>
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      </button>
                    ))}
                  </div>
                </SheetContent>
              </Sheet>

              {/* Online dot */}
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            </div>
          </div>

          {/* New Chat — only shown when conversation is active */}
          {messages.length > 0 && (
            <div className="mt-2 flex">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearConversation}
                disabled={isLoading}
                className="h-7 px-2 text-[11px] text-muted-foreground gap-1 -ml-1"
              >
                <RotateCcw className="h-3 w-3" />
                New Chat
              </Button>
            </div>
          )}
        </div>

        {/* ── Scroll area ────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {isEmpty ? (
            /* ── Empty state ─────────────────────────────────────────────── */
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="px-4 pt-8 pb-4 space-y-6"
            >
              {/* Greeting */}
              <div className="flex flex-col items-center text-center gap-3 pb-2">
                <div className="flex items-center justify-center w-14 h-14 rounded-3xl bg-violet-500/10">
                  <Sparkles className="h-7 w-7 text-violet-600" />
                </div>
                <div>
                  <p className="text-xl font-semibold leading-tight">
                    {getGreeting()}, {firstName}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Ask anything about your leads &amp; performance
                  </p>
                </div>
              </div>

              {/* Recent queries (if any) */}
              {recentQueries.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2 px-1">
                    Recent
                  </p>
                  <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
                    {recentQueries.slice(0, 5).map((q, i) => (
                      <button
                        key={i}
                        onClick={() => sendQuery(q)}
                        className="flex-none flex items-center gap-1.5 h-8 px-3 rounded-full border border-border/70 bg-card text-xs text-muted-foreground hover:bg-muted/60 active:scale-95 whitespace-nowrap transition-all"
                      >
                        <Clock className="h-3 w-3 shrink-0" />
                        <span className="truncate max-w-[160px]">{q}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Top 3 suggestions */}
              <div className="space-y-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-1">
                  Try asking
                </p>
                {[
                  "Total disbursement amount this month",
                  "Top 5 bankers by disbursement this month",
                  "Which banker has the highest conversion rate?",
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => sendQuery(suggestion)}
                    disabled={isLoading}
                    className="w-full text-left px-4 py-3.5 rounded-2xl border border-border/60 bg-card hover:bg-muted/50 active:scale-[0.99] transition-all text-sm text-foreground/80 hover:text-foreground shadow-sm disabled:opacity-40"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </motion.div>
          ) : (
            /* ── Conversation ─────────────────────────────────────────────── */
            <div className="px-3 py-3 space-y-3">
              <AnimatePresence initial={false}>
                {messages.map((msg) => (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.18 }}
                    className={cn(
                      "flex gap-2 items-end",
                      msg.role === "user" ? "flex-row-reverse" : "flex-row",
                    )}
                  >
                    {/* Avatar */}
                    <div
                      className={cn(
                        "flex items-center justify-center w-6 h-6 rounded-full shrink-0 mb-0.5",
                        msg.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-violet-500/10 border border-violet-200/40",
                      )}
                    >
                      {msg.role === "user" ? (
                        <User className="h-3 w-3" />
                      ) : (
                        <Bot className="h-3 w-3 text-violet-600" />
                      )}
                    </div>

                    {/* Bubble + card + follow-ups — assistant fills near-full width */}
                    <div
                      className={cn(
                        "flex flex-col gap-2",
                        msg.role === "user"
                          ? "max-w-[80%]"
                          : "max-w-[92%] w-full",
                      )}
                    >
                      {/* Text bubble — only shown when content exists */}
                      {msg.content && (
                        <div
                          className={cn(
                            "rounded-2xl px-3.5 py-2.5 text-sm shadow-sm",
                            msg.role === "user"
                              ? "bg-primary text-primary-foreground rounded-br-sm"
                              : "bg-muted border border-border/60 text-foreground rounded-bl-sm",
                          )}
                        >
                          {msg.role === "assistant" ? (
                            <div className="max-w-none text-sm leading-relaxed">
                              <ReactMarkdown
                                components={{
                                  p: ({ children }) => (
                                    <p className="mb-2 last:mb-0 leading-relaxed">
                                      {children}
                                    </p>
                                  ),
                                  ul: ({ children }) => (
                                    <ul className="my-2 space-y-1 list-none">
                                      {children}
                                    </ul>
                                  ),
                                  ol: ({ children }) => (
                                    <ol className="my-2 space-y-1 list-decimal ml-4">
                                      {children}
                                    </ol>
                                  ),
                                  li: ({ children, ...props }) => {
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    const isOrdered = (props as any).ordered;
                                    if (isOrdered) {
                                      return (
                                        <li className="leading-relaxed pl-0.5">
                                          {children}
                                        </li>
                                      );
                                    }
                                    return (
                                      <li className="flex items-start gap-2">
                                        <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-violet-500 shrink-0" />
                                        <span className="leading-relaxed">
                                          {children}
                                        </span>
                                      </li>
                                    );
                                  },
                                  strong: ({ children }) => (
                                    <strong className="font-semibold text-foreground">
                                      {children}
                                    </strong>
                                  ),
                                  em: ({ children }) => (
                                    <em className="italic text-muted-foreground">
                                      {children}
                                    </em>
                                  ),
                                  h1: ({ children }) => (
                                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mt-3 mb-1 first:mt-0">
                                      {children}
                                    </p>
                                  ),
                                  h2: ({ children }) => (
                                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mt-3 mb-1 first:mt-0">
                                      {children}
                                    </p>
                                  ),
                                  h3: ({ children }) => (
                                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mt-2 mb-1 first:mt-0">
                                      {children}
                                    </p>
                                  ),
                                  hr: () => (
                                    <hr className="my-2 border-border/50" />
                                  ),
                                  a: ({ href, children }) => {
                                    if (!href) return <span>{children}</span>;
                                    const safeHref = (() => {
                                      try {
                                        const u = new URL(href, window.location.origin);
                                        return u.protocol === "http:" || u.protocol === "https:" ? u.toString() : null;
                                      } catch {
                                        return null;
                                      }
                                    })();
                                    if (!safeHref) return <span>{children}</span>;
                                    const fileType = getFileType(safeHref);
                                    const fileName = (() => {
                                      try { return decodeURIComponent(new URL(safeHref).pathname.split("/").pop() ?? String(children)); }
                                      catch { return String(children); }
                                    })();
                                    const canPreview =
                                      fileType === "image" ||
                                      fileType === "pdf";
                                    return (
                                      <span className="inline-flex items-center gap-1">
                                        {canPreview ? (
                                          <button
                                            type="button"
                                            onClick={() => setPreviewFile({ url: safeHref, name: fileName, type: fileType })}
                                            className="underline underline-offset-2 text-left hover:text-violet-600 transition-colors"
                                          >
                                            {children}
                                          </button>
                                        ) : (
                                          <a href={safeHref} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">{children}</a>
                                        )}
                                        <a href={safeHref} download target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors" title="Download">
                                          <Download className="h-3 w-3 inline" />
                                        </a>
                                      </span>
                                    );
                                  },
                                }}
                              >
                                {msg.content}
                              </ReactMarkdown>
                              {msg.id === streamingMsgId && (
                                <span className="inline-block w-[2px] h-[1em] bg-foreground/50 animate-pulse align-text-bottom ml-px" />
                              )}
                            </div>
                          ) : (
                            <p className="whitespace-pre-wrap leading-relaxed">
                              {msg.content}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Copy the question back out. Rendered as its own row
                          rather than on hover — this screen is touch-first. */}
                      {msg.role === "user" && msg.content && (
                        <button
                          type="button"
                          onClick={() => handleCopyMessage(msg.id, msg.content)}
                          className="self-end flex items-center gap-1 -mt-1 px-1.5 py-1 rounded-lg text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted/60 active:scale-95 transition-all"
                          aria-label="Copy message"
                        >
                          {copiedId === msg.id ? (
                            <>
                              <Check className="h-3 w-3" />
                              Copied
                            </>
                          ) : (
                            <>
                              <Copy className="h-3 w-3" />
                              Copy
                            </>
                          )}
                        </button>
                      )}

                      {/* Rich data cards for lookup/analytical results — shown only after streaming finishes */}
                      {msg.role === "assistant" &&
                        msg.id !== streamingMsgId &&
                        msg.data?.rows &&
                        msg.data.rows.length > 0 && (
                          <AnswerCard
                            rows={msg.data.rows}
                            resultType={msg.data.resultType}
                            entity={msg.data.entity}
                          />
                        )}

                      {/* Product notes deep link — the product track answers in
                          prose, so there is no record card to carry an Open
                          button. */}
                      {msg.role === "assistant" &&
                        msg.id !== streamingMsgId &&
                        msg.productRef && (
                          <ProductNotesLink productRef={msg.productRef} />
                        )}

                      {/* Follow-up chips */}
                      {msg.role === "assistant" &&
                        msg.followUpQuestions &&
                        msg.followUpQuestions.length > 0 && (
                          <div className="flex flex-col gap-1.5">
                            {msg.followUpQuestions.map((q) => (
                              <button
                                key={q}
                                onClick={() => sendQuery(q)}
                                disabled={isLoading}
                                className="text-left text-xs px-3 py-2 rounded-xl border border-violet-300/40 bg-violet-500/5 hover:bg-violet-500/10 text-violet-700 dark:text-violet-400 transition-colors active:scale-[0.98] disabled:opacity-40"
                              >
                                {q}
                              </button>
                            ))}
                          </div>
                        )}
                    </div>
                  </motion.div>
                ))}

                {/* Typing indicator */}
                {isLoading && !streamingMsgId && (
                  <motion.div
                    key="typing"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="flex gap-2 items-end"
                  >
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-violet-500/10 border border-violet-200/40 shrink-0">
                      <Bot className="h-3 w-3 text-violet-600" />
                    </div>
                    <TypingIndicator step={loadingStep} />
                  </motion.div>
                )}
              </AnimatePresence>
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* ── Bottom sticky — Input ──────────────────────── */}
        <div className="shrink-0 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          {/* Autocomplete chips (shown while typing) */}
          {autocompletes.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1 pt-2 px-4 scrollbar-none">
              {autocompletes.map((ac) => (
                <button
                  key={ac}
                  onClick={() => sendQuery(ac)}
                  disabled={isLoading}
                  className="flex-none h-8 px-3 rounded-full bg-violet-500/10 text-violet-700 dark:text-violet-400 text-xs font-medium border border-violet-300/30 active:scale-95 transition-transform whitespace-nowrap disabled:opacity-40"
                >
                  {ac}
                </button>
              ))}
            </div>
          )}

          {/* Input row */}
          <div className="flex items-end gap-2 px-3 pt-2 pb-3">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                rows={1}
                value={query}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                placeholder="Ask something…"
                className="w-full resize-none rounded-2xl border border-input bg-muted/40 px-4 py-3 pr-3 text-[15px] placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-violet-500/40 focus:border-transparent disabled:opacity-50 transition-all leading-relaxed"
                style={{ minHeight: "48px", maxHeight: "100px" }}
              />
            </div>
            <Button
              size="icon"
              aria-label="Send message"
              onClick={() => sendQuery(query)}
              disabled={isLoading || !query.trim()}
              className="rounded-full h-11 w-11 shrink-0 bg-violet-600 hover:bg-violet-700 active:bg-violet-800 text-white shadow-md transition-colors"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>

          {/* Safe-area padding is already reserved by AppLayout + MobileNav. */}
          <div className="h-1" />
        </div>
      </div>

      {/* ── File preview dialog ─────────────────────────────────────────── */}
      <Dialog
        open={!!previewFile}
        onOpenChange={(open) => {
          if (!open) setPreviewFile(null);
        }}
      >
        <DialogContent className="max-w-3xl w-[95vw] p-0 gap-0 overflow-hidden">
          <DialogHeader className="flex flex-row items-center justify-between px-4 py-3 border-b shrink-0 space-y-0">
            <div className="flex items-center gap-2 min-w-0">
              {previewFile?.type === "image" ? (
                <ImageIcon className="h-4 w-4 text-muted-foreground shrink-0" />
              ) : (
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
              <DialogTitle className="text-sm font-medium truncate max-w-[60vw]">
                {previewFile?.name}
              </DialogTitle>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <a
                href={previewFile?.url}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded-md hover:bg-muted transition-colors"
                title="Download"
              >
                <Download className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Download</span>
              </a>
              <a
                href={previewFile?.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded-md hover:bg-muted transition-colors"
                title="Open in new tab"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Open</span>
              </a>
            </div>
          </DialogHeader>

          <div
            className="overflow-auto bg-muted/30"
            style={{ maxHeight: "80vh" }}
          >
            {previewFile?.type === "image" && (
              <div className="flex items-center justify-center p-4">
                <img
                  src={previewFile.url}
                  alt={previewFile.name}
                  className="max-w-full max-h-[70vh] object-contain rounded-md"
                />
              </div>
            )}
            {previewFile?.type === "pdf" && (
              <iframe
                src={previewFile.url}
                title={previewFile.name}
                className="w-full border-0"
                style={{ height: "75vh" }}
              />
            )}
            {previewFile?.type === "other" && (
              <div className="flex flex-col items-center justify-center gap-4 py-16 text-muted-foreground">
                <FileText className="h-12 w-12 opacity-40" />
                <p className="text-sm">
                  This file type can't be previewed in the app.
                </p>
                <a
                  href={previewFile.url}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Download file
                </a>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};
